import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'framer-motion';
import { ChevronDownIcon } from 'lucide-react';
interface FaqItem {
  question: string;
  answer: string;
}
const faqs: FaqItem[] = [{
  question: 'Is this too generic? I teach a very specific age group.',
  answer: "Every module is built for a specific age band. You use what applies to you. Everything is organized so you never dig through content that doesn't belong to your classroom."
}, {
  question: 'I have bought curriculum packs before and never used them.',
  answer: 'This pack opens at your grade level and your current week. The first action you take is adding your name to a lesson plan that is already written. It is built to be used the day you download it.'
}, {
  question: 'I am a new teacher. Is this too advanced for me?',
  answer: 'It is especially for you. The pack removes the planning burden so you can focus on building real relationships with real students.'
}, {
  question: 'What if my school has no instruments?',
  answer: 'A significant portion of the activities require no materials at all. Voice, body, clapping, and movement are the instruments. The DIY instrument guide gives you a full classroom percussion section for zero dollars.'
}, {
  question: 'I have been teaching for fifteen years. Do I really need this?',
  answer: 'The pack gives you a base you can build on and adapt. The Activities Bank and Assessment Folder alone give you infrastructure that many experienced teachers have spent years building piecemeal.'
}];
export function FaqSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  return <section id="faq" ref={ref} className="w-full bg-white py-8 md:py-16 px-4" data-id="element-64">
      <div className="max-w-4xl mx-auto" data-id="element-65">
        <motion.h2 initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 30
      }} transition={{
        duration: 0.6
      }} className="text-3xl md:text-4xl lg:text-5xl font-bold text-navy text-center mb-8 md:mb-12" data-id="element-66">
          FREQUENTLY ASKED QUESTIONS
        </motion.h2>

        <div className="space-y-3 md:space-y-4" data-id="element-67">
          {faqs.map((faq, index) => <FaqItem key={index} faq={faq} index={index} isOpen={openIndex === index} onClick={() => setOpenIndex(openIndex === index ? null : index)} isInView={isInView} data-id="element-68" />)}
        </div>
      </div>
    </section>;
}
interface FaqItemProps {
  faq: FaqItem;
  index: number;
  isOpen: boolean;
  onClick: () => void;
  isInView: boolean;
}
function FaqItem({
  faq,
  index,
  isOpen,
  onClick,
  isInView
}: FaqItemProps) {
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={isInView ? {
    opacity: 1,
    y: 0
  } : {
    opacity: 0,
    y: 20
  }} transition={{
    duration: 0.4,
    delay: index * 0.1
  }} className="border-2 border-gold rounded-lg overflow-hidden" data-id="element-69">
      <button onClick={onClick} className="w-full px-4 md:px-6 py-3 md:py-4 flex items-center justify-between bg-cream hover:bg-gold-light transition-colors text-left" aria-expanded={isOpen} data-id="element-70">
        <span className="text-base md:text-lg font-semibold text-navy pr-4" data-id="element-71">
          {faq.question}
        </span>
        <motion.div animate={{
        rotate: isOpen ? 180 : 0
      }} transition={{
        duration: 0.3
      }} data-id="element-72">
          <ChevronDownIcon className="w-5 h-5 md:w-6 md:h-6 text-navy flex-shrink-0" aria-hidden="true" data-id="element-73" />
        </motion.div>
      </button>
      <AnimatePresence data-id="element-74">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3
      }} className="overflow-hidden" data-id="element-75">
            <div className="px-4 md:px-6 py-3 md:py-4 bg-white text-base md:text-lg leading-relaxed" data-id="element-76">
              {faq.answer}
            </div>
          </motion.div>}
      </AnimatePresence>
    </motion.div>;
}